import { useEffect, useState, useMemo } from 'react';
import {
    Row,
    Col,
    Breadcrumb,
    Button,
    Space,
    Badge,
    Drawer,
    List,
    Typography,
    Tag,
    Empty,
    Spin,
} from 'antd';
import {
    PoweroffOutlined,
    BellOutlined,
    InboxOutlined,
    UserOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    SyncOutlined,
    DollarOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { NavLink, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useNotifications from '../../hooks/useNotifications';

const { Text, Paragraph } = Typography;

const toggler = [
    <svg
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 448 512"
        key={0}
    >
        <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z"></path>
    </svg>,
];

/**
 * Get icon for notification type
 */
const getNotificationIcon = (type) => {
    switch (type) {
        case 'document':
            return <FileTextOutlined style={{ color: '#1890ff' }} />;
        case 'alert':
            return <WarningOutlined style={{ color: '#ff4d4f' }} />;
        case 'transaction':
            return <DollarOutlined style={{ color: '#52c41a' }} />;
        case 'user':
            return <UserOutlined style={{ color: '#722ed1' }} />;
        case 'currency':
            return <DollarOutlined style={{ color: '#faad14' }} />;
        case 'system':
        default:
            return <SyncOutlined style={{ color: '#1890ff' }} />;
    }
};

/**
 * Get tag for notification type
 */
const getNotificationTag = (type) => {
    switch (type) {
        case 'document':
            return <Tag color="processing">Document</Tag>;
        case 'alert':
            return <Tag color="error">Alert</Tag>;
        case 'transaction':
            return <Tag color="success">Transaction</Tag>;
        case 'user':
            return <Tag color="purple">User</Tag>;
        case 'currency':
            return <Tag color="warning">Currency</Tag>;
        case 'system':
        default:
            return <Tag color="default">System</Tag>;
    }
};

function Header({
    placement,
    name,
    subName,
    onPress,
    handleLogout,
    handleSidenavColor,
    handleSidenavType,
    handleFixedNavbar,
}) {
    const { user } = useAuth();
    const [notificationDrawer, setNotificationDrawer] = useState(false);
    const [inboxDrawer, setInboxDrawer] = useState(false);

    // Fetch notifications from API
    const {
        notifications,
        unreadCount,
        isFetching,
        isPending,
        isApiUnavailable,
        markAsRead,
        markAllAsRead,
    } = useNotifications();

    // Filter notifications into alerts/notifications and inbox messages
    const { alertNotifications, inboxMessages } = useMemo(() => {
        const alerts = [];
        const inbox = [];

        notifications.forEach((notification) => {
            // Consider 'email' type notifications as inbox messages
            if (notification.raw?.type === 'email') {
                inbox.push(notification);
            } else {
                alerts.push(notification);
            }
        });

        return { alertNotifications: alerts, inboxMessages: inbox };
    }, [notifications]);

    // Calculate unread counts separately
    const unreadAlertCount = useMemo(
        () => alertNotifications.filter((n) => !n.isRead).length,
        [alertNotifications]
    );
    const unreadInboxCount = useMemo(
        () => inboxMessages.filter((n) => !n.isRead).length,
        [inboxMessages]
    );

    // Mark notifications as read when drawer opens
    const handleNotificationDrawerOpen = () => {
        setNotificationDrawer(true);
    };

    const handleNotificationDrawerClose = () => {
        setNotificationDrawer(false);
        // Mark all alert notifications as read when closing the drawer
        alertNotifications.forEach((n) => {
            if (!n.isRead) {
                markAsRead(n.id);
            }
        });
    };

    const handleInboxDrawerOpen = () => {
        setInboxDrawer(true);
    };

    const handleInboxDrawerClose = () => {
        setInboxDrawer(false);
        // Mark all inbox messages as read when closing the drawer
        inboxMessages.forEach((n) => {
            if (!n.isRead) {
                markAsRead(n.id);
            }
        });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Row gutter={[24, 0]}>
                <Col
                    span={24}
                    md={6}
                >
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <NavLink to="/">Pages</NavLink>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item
                            style={{ textTransform: 'capitalize' }}
                        >
                            {name.replace('/', '')}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <div className="ant-page-header-heading">
                        <span
                            className="ant-page-header-heading-title"
                            style={{ textTransform: 'capitalize' }}
                        >
                            {subName.replace('/', '')}
                        </span>
                    </div>
                </Col>
                <Col
                    span={24}
                    md={18}
                    className="header-control"
                >
                    <Button
                        type="link"
                        className="sidebar-toggler"
                        onClick={() => onPress()}
                    >
                        {toggler}
                    </Button>
                    {user ? (
                        <Space size="middle">
                            <Badge count={unreadAlertCount}>
                                <Button
                                    icon={<BellOutlined />}
                                    onClick={handleNotificationDrawerOpen}
                                    loading={isPending}
                                />
                            </Badge>
                            <Badge count={unreadInboxCount}>
                                <Button
                                    icon={<InboxOutlined />}
                                    onClick={handleInboxDrawerOpen}
                                    loading={isPending}
                                />
                            </Badge>
                            <Button
                                onClick={handleLogout}
                                type="default"
                                icon={<PoweroffOutlined />}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '4px 15px',
                                    height: '40px',
                                    borderRadius: '6px',
                                    transition: 'all 0.3s',
                                    backgroundColor: '#fff',
                                    border: '1px solid #d9d9d9',
                                    boxShadow: '0 2px 0 rgba(0,0,0,0.02)',
                                    gap: '8px',
                                }}
                                hover={{
                                    color: '#1890ff',
                                    borderColor: '#1890ff',
                                }}
                            >
                                <span>Logout</span>
                            </Button>
                        </Space>
                    ) : (
                        <Link
                            to="/sign-in"
                            className="btn-sign-in"
                        >
                            <PoweroffOutlined />
                            <span>Sign in</span>
                        </Link>
                    )}
                </Col>
            </Row>

            {/* Notification Drawer */}
            <Drawer
                title={
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text strong>Notifications</Text>
                        {unreadAlertCount > 0 && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    alertNotifications.forEach((n) => markAsRead(n.id));
                                }}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Space>
                }
                placement="right"
                onClose={handleNotificationDrawerClose}
                open={notificationDrawer}
                width={380}
            >
                {isPending ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : isApiUnavailable ? (
                    <Empty
                        description="Notifications are not available yet. The API endpoint is not implemented."
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : alertNotifications.length === 0 ? (
                    <Empty
                        description="No notifications"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <List
                    itemLayout="vertical"
                        dataSource={alertNotifications}
                    renderItem={(item) => (
                        <List.Item
                                extra={getNotificationTag(item.type)}
                            style={{
                                padding: '16px 0',
                                borderBottom: '1px solid #f0f0f0',
                                    backgroundColor: item.isRead
                                        ? 'transparent'
                                        : 'rgba(24, 144, 255, 0.04)',
                                    cursor: 'pointer',
                            }}
                                onClick={() => markAsRead(item.id)}
                        >
                            <List.Item.Meta
                                    avatar={getNotificationIcon(item.type)}
                                    title={
                                        <Text
                                            strong={!item.isRead}
                                            style={{
                                                opacity: item.isRead ? 0.7 : 1,
                                            }}
                                        >
                                            {item.title}
                                        </Text>
                                    }
                                description={
                                    <Space
                                        direction="vertical"
                                        size={4}
                                    >
                                            <Paragraph
                                                style={{
                                                    margin: 0,
                                                    opacity: item.isRead ? 0.7 : 1,
                                                }}
                                            >
                                            {item.description}
                                        </Paragraph>
                                        <Text
                                            type="secondary"
                                            italic
                                        >
                                            {item.time}
                                        </Text>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
                )}
            </Drawer>

            {/* Inbox Drawer */}
            <Drawer
                title={
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Text strong>Inbox</Text>
                        {unreadInboxCount > 0 && (
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    inboxMessages.forEach((n) => markAsRead(n.id));
                                }}
                            >
                                Mark all as read
                            </Button>
                        )}
                    </Space>
                }
                placement="right"
                onClose={handleInboxDrawerClose}
                open={inboxDrawer}
                width={380}
            >
                {isPending ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : isApiUnavailable ? (
                    <Empty
                        description="Inbox is not available yet. The API endpoint is not implemented."
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : inboxMessages.length === 0 ? (
                    <Empty
                        description="No messages"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                ) : (
                    <List
                        itemLayout="vertical"
                        dataSource={inboxMessages}
                    renderItem={(item) => (
                        <List.Item
                                extra={getNotificationTag(item.type)}
                            style={{
                                padding: '16px 0',
                                borderBottom: '1px solid #f0f0f0',
                                    backgroundColor: item.isRead
                                        ? 'transparent'
                                        : 'rgba(24, 144, 255, 0.04)',
                                    cursor: 'pointer',
                            }}
                                onClick={() => markAsRead(item.id)}
                        >
                            <List.Item.Meta
                                    avatar={getNotificationIcon(item.type)}
                                    title={
                                        <Text
                                            strong={!item.isRead}
                                            style={{
                                                opacity: item.isRead ? 0.7 : 1,
                                            }}
                                        >
                                            {item.title}
                                        </Text>
                                    }
                                description={
                                    <Space
                                        direction="vertical"
                                        size={4}
                                    >
                                            <Paragraph
                                                style={{
                                                    margin: 0,
                                                    opacity: item.isRead ? 0.7 : 1,
                                                }}
                                            >
                                                {item.description}
                                        </Paragraph>
                                        <Text
                                            type="secondary"
                                            italic
                                        >
                                            {item.time}
                                        </Text>
                                    </Space>
                                }
                            />
                        </List.Item>
                    )}
                />
                )}
            </Drawer>
        </>
    );
}

export default Header;
